import React, { createContext, useMemo, useEffect, useState, useContext, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'

import {
    useIntl,
    useToggle,
    useConfirm,
    useFileFavorites,
    useSelectable,
    useFileDownload,
    useFileDelete,
} from 'hooks'
import { generateQuery } from 'utils/filter'
import { PATIENT_FILE_FILTERS } from 'app/constants'
import { useGetPatientFilesWithSummaryQuery } from 'services'
import { set } from 'features/file-view/fileSlice'
import { PatientContext } from 'features/surgical-cloud/PatientsCollections'
import { patientSelector } from 'features/patients-list/patientSlice'
import AddToCollection from 'features/add-files-collection'
import Tags from 'features/tags'
import Share from 'features/share/Share'
import { Loader } from 'components/Loaders'
import SelectedFileActionsComponent from 'components/SelectedFileActions'
import PatientDetailsHeader from 'components/Patient/PatientDetailsHeader'
import PatientDetailsFallback from 'components/Fallback/Details'
import FilesFilter from 'components/FilesUIFilter'
import Treatment from './Treatment'
import { Checkbox, ScrollableDirective } from 'components/Zui'

export const FileContext = createContext(null)

const Details = () => {
    const { t } = useIntl()
    const history = useHistory()
    const reduxDispatch = useDispatch()
    const [filter, setFilter] = useState(null)
    const [tagData, setTagData] = useState({})
    const [addCollectionData, setAddCollectionData] = useState({})
    const [sharablePatient, setShareablePatient] = useState()
    const { patient } = useContext(PatientContext)
    const { getConfirmation } = useConfirm()
    const patientId = patient?.id
    const {
        isOpen: isCollectionOpen,
        onOpen: onCollectionOpen,
        onClose: onCollectionClose,
    } = useToggle()
    const { isOpen: isTagOpen, onOpen: onTagOpen, onClose: onTagClose } = useToggle()
    const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useToggle()

    const patientsIds = useSelector(state => patientSelector.selectIds(state))
    const { filterParams, sort, uiFilter } = useSelector(state => state.patients)
    const { download } = useFileDownload('files')
    const { deleteFiles } = useFileDelete('files')

    /*
     *  Data will be cashed for 20 minutes (1200 ms).
     *  To update cache time, change value of `keepUnusedDataFor` in service endpoint
     */
    const { data, isLoading, isFetching, isError } = useGetPatientFilesWithSummaryQuery(
        {
            id: patientId,
            query: generateQuery(filterParams.filter(p => ['sort', 'filter'].includes(p.type))),
        },
        {
            skip: !patientId,
        }
    )

    const { actions, dispatch, selectable, handleSelectFile, handleSelectAll, isSelectedAll } =
        useSelectable(data?.files, filter)

    const { isAllFavorite, favoriteFileIds, toggleFavorite, toggleAllFavorite } = useFileFavorites(
        data?.files,
        selectable.selectedFiles
    )

    const sortedDates = data?.dates
        .slice()
        .sort((a, b) =>
            sort === 'dateAsc' ? new Date(a) - new Date(b) : new Date(b) - new Date(a)
        )

    useEffect(() => {
        setFilter(uiFilter)
        dispatch(actions.unSelectAll())
    }, [patientId])

    const getSingleFileType = useCallback(() => {
        if (selectable.selectedFiles.length > 1) return
        const fileId = selectable.selectedFiles[0]
        return data?.files.find(f => f.fileId === fileId).fileType.toLowerCase()
    }, [selectable, data])

    const getFilteredFileIds = useCallback(() => {
        let fileIds = []
        if (filter !== 'all') {
            fileIds = data?.files.filter(t => t.fileType === filter).map(t => t.fileId) || []
        } else {
            fileIds = data?.files.map(f => f.fileId) || []
        }
        return fileIds
    }, [filter, data])

    const handleFileView = useCallback(
        id => {
            const fileIds = getFilteredFileIds()
            const viewFiles = data?.files.filter(f => fileIds.includes(f.fileId)) || []

            reduxDispatch(set(viewFiles))
            history.push({
                pathname: '/files/' + id,
                key: id,
                state: {
                    actionType: 'file-view',
                    tab: 'patients',
                    id: patient,
                    filter,
                },
            })
        },
        [filter, data]
    )

    const handleAddFiles = () => {
        setAddCollectionData({
            type: 'files',
            fileIds: selectable.selectedFiles,
        })
        onCollectionOpen()
    }

    const handleAddPatientFiles = () => {
        setAddCollectionData({
            type: 'patient',
            subType: filter,
            id: patientId,
        })
        onCollectionOpen()
    }

    const handleTagFiles = () => {
        setTagData({
            type: 'files',
            fileIds: selectable.selectedFiles,
        })
        onTagOpen()
    }

    const handleTagPatient = () => {
        setTagData({
            type: 'patient',
            subType: filter,
            id: patientId,
        })
        onTagOpen()
    }

    const handleSharePatient = () => {
        setShareablePatient({
            type: 'patient',
            subType: filter,
            id: patientId,
            name: patient.name,
        })
        onShareOpen()
    }

    const handleShareFiles = () => {
        const length = selectable.selectedFiles.length

        setShareablePatient({
            type: 'files',
            fileIds: selectable.selectedFiles,
            fileType: getSingleFileType(),
            name: length > 1 ? length + ' files' : length === 1 ? length + ' file' : null,

            patient: {
                id: patientId,
                name: patient.name,
            },
        })
        onShareOpen()
    }

    const handleDownloadPatient = () => {
        download({
            type: 'patient',
            subType: filter,
            id: patientId,
        })
    }

    const handleDownloadFiles = () => {
        download({
            fileIds: selectable.selectedFiles,
        })
        handleSelectFile(false, selectable.selectedFiles)
    }

    const handleDeletePatient = async () => {
        const confirmed = await getConfirmation({
            title: t('patient.confirm.delete.title'),
            message: t('patient.confirm.delete.message'),
            buttons: [t('cancel'), t('delete')],
        })

        if (!confirmed) return

        deleteFiles({
            type: 'patient',
            subType: filter,
            id: patientId,
        })
    }

    const handleDeleteFiles = async () => {
        const confirmed = await getConfirmation({
            title: t('patient.files.confirm.delete.title'),
            message: t('patient.files.confirm.delete.message'),
            buttons: [t('cancel'), t('delete')],
        })

        if (!confirmed) return

        deleteFiles({
            id: patientId,
            fileIds: selectable.selectedFiles,
        })
    }

    const handleAllFavorite = () => {
        toggleAllFavorite(true)
    }

    const selectedFileActions = [
        {
            icon: 'share',
            onClick: handleShareFiles,
        },
        {
            icon: isAllFavorite ? 'favoriteFilled' : 'favorite',
            onClick: handleAllFavorite,
        },
        {
            icon: 'moveFolder',
            onClick: handleAddFiles,
        },
        {
            icon: 'tag',
            onClick: handleTagFiles,
        },
        {
            icon: 'download',
            onClick: handleDownloadFiles,
        },
        {
            icon: 'delete',
            onClick: handleDeleteFiles,
        },
    ]

    const RenderTreatments = useMemo(
        () => (
            <div className="treatment-details-wrapper">
                <ScrollableDirective style={{ height: '100%' }}>
                    {sortedDates?.map((date, index) => (
                        <Treatment
                            data={{ acquisitionDate: date }}
                            key={index}
                            defaultOpen={index === 0}
                            filter={filter}
                            files={data.studies[date]}
                            handleFavorite={toggleFavorite}
                            handleClick={handleFileView}
                            handleSelectFile={handleSelectFile}
                        />
                    ))}
                </ScrollableDirective>
            </div>
        ),
        [patientId, data, filter, selectable, favoriteFileIds]
    )

    if (!patient && !patientsIds.length) {
        return (
            <div className="content-container">
                <PatientDetailsFallback title={t('fallback.no.media')} />
            </div>
        )
    }

    const filterOptions = PATIENT_FILE_FILTERS.map(op => {
        return { ...op, count: data?.summary ? data.summary[op.value] : 0 }
    })

    return (
        <FileContext.Provider value={{ selectable, actions, dispatch }}>
            <div className="content-container">
                {patient ? (
                    <div className="patient-details-wrapper">
                        <div className="patient-contents-header">
                            <PatientDetailsHeader
                                onDelete={handleDeletePatient}
                                onAddTag={handleTagPatient}
                                onShare={handleSharePatient}
                                onDownload={handleDownloadPatient}
                                onAddToCollection={handleAddPatientFiles}
                            />
                            <div className="filter-select-options-wrapper">
                                <div className="files-filter-wrapper">
                                    <FilesFilter
                                        isDisabled={!Boolean(data?.dates.length)}
                                        value={filter}
                                        onChange={setFilter}
                                        options={filterOptions}
                                    />
                                    <Checkbox
                                        style={{ display: ' flex', fontSize: ' 14px' }}
                                        theme="zbds-dark"
                                        name="selectAll"
                                        disabled={Boolean(!sortedDates?.length)}
                                        value={isSelectedAll}
                                        onChange={e => handleSelectAll(e.target.value)}
                                    >
                                        <span style={{ color: '#D1D9E5' }}>{t('select.all')}</span>
                                    </Checkbox>
                                </div>
                                {Boolean(
                                    selectable.selectedFiles.length && data?.summary[filter]
                                ) && <SelectedFileActionsComponent actions={selectedFileActions} />}
                            </div>
                        </div>

                        {(isLoading || isFetching) && <Loader />}
                        {!isLoading && !isFetching && (!data?.dates.length || isError) && (
                            <PatientDetailsFallback title={t('fallback.no.media')} />
                        )}
                        {!isLoading && !isFetching && data?.dates.length && RenderTreatments}
                    </div>
                ) : (
                    <PatientDetailsFallback title={t('fallback.no.patient.selected')} icon="user" />
                )}

                {isCollectionOpen && (
                    <AddToCollection
                        onClose={onCollectionClose}
                        handleSelectFile={handleSelectFile}
                        data={addCollectionData}
                    />
                )}
                {isTagOpen && <Tags onClose={onTagClose} data={tagData} />}
                {isShareOpen && <Share onClose={onShareClose} data={sharablePatient} />}
            </div>
        </FileContext.Provider>
    )
}

export default React.memo(Details)
